import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { login } from "../../../redux/slice/sso/authentication-slice";
import { RootState, useAppDispatch } from "../../../redux/store";

import useAuth from "../../../hooks/useAuth";

import LoginHelpModal from "./404-login-help-modal.component";
import { retrieveUserInfo } from "../../../redux/slice/sso/userinfo.slice";
import LoadingComp from "../../../components/re-usable/loading/loading";

interface FormElements extends HTMLFormControlsCollection {
  txtText: HTMLInputElement,
  txtPassword: HTMLInputElement
}

interface LoginFormElement extends HTMLFormElement {
  readonly elements: FormElements
}

interface LoginRequest {
  username: string,
  password: string,
  staySignedIn: boolean
}

const ErrorLoginComponent = (props: {isLoginPage: boolean}) => {
  const { isLoginPage } = props;
  const { loginUser } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [attemptedLoggedIn, setAttemptedLoggedIn] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const { checkLogin } = useSelector(
    (state: RootState) => state.loggedIn
  );
  const loginData = useSelector((state: RootState) => state.login.data);
  const userInfo = useSelector((state: RootState) => state.userInfo);

  const location = useLocation();
  const navigate = useNavigate();
  const { from } = location.state || { from: { pathname: "/member-dashboard" } };

  const dispatch = useAppDispatch();

  const { search } = location;
  const query = React.useMemo(() => new URLSearchParams(search.toLowerCase()), [search]);

  useEffect(() => {
      if (!checkLogin.isLoggedIn && attemptedLoggedIn) {
      let request: LoginRequest = {
        username: username,
        password: password,
        staySignedIn: false
      }
      dispatch(login(request));
      setShowErrorMessage(false);
    }
  }, [attemptedLoggedIn]);

  useEffect(() => {
    if (attemptedLoggedIn && loginData.length > 0) {
      loginUser(loginData[0]);

      if (window.runGlobalScripts) {
        window.runGlobalScripts();
      }
    }
      
    else if (attemptedLoggedIn) {
        setAttemptedLoggedIn(false);
        setShowErrorMessage(true);
    }
  }, [loginData]);

  useEffect(() => {
    if (checkLogin.isLoggedIn) {
      if (!userInfo.loading && userInfo.data?.result === undefined) {
        dispatch(retrieveUserInfo());
      }

      if (attemptedLoggedIn || isLoginPage) {
        setAttemptedLoggedIn(false);
        let returnUrl = query.get("returnurl") || null;
        if (returnUrl !== null) {
          window.location.replace(returnUrl);
        } else {
          navigate(from);
        }
      }
    }
  }, [checkLogin]);

  const handleSubmit = (event: React.FormEvent<LoginFormElement>) => {
    event.preventDefault()
    setUsername(event.currentTarget.elements.txtText.value);
    setPassword(event.currentTarget.elements.txtPassword.value);
    setAttemptedLoggedIn(true);
  }

  if (checkLogin.isLoggedIn && !isLoginPage) {
    return (<></>)
  }

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setShowModal(true);
  }

  const handleModalEvent = () => {
    setShowModal(false);
  }

  return (
    <>
      <div className="FormPanel">
        <p className="pull-right">
          <a data-lity="" href="#login_help" onClick={onClick}>
            <svg className="icomoon icomoon-help">
              <use xlinkHref="#icomoon-help" xmlnsXlink="http://www.w3.org/1999/xlink"></use>
            </svg>
            <span className="sr-only">help</span>
          </a>
        </p>

        <h2>Log in</h2>

        <div className="form-login">
          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="EditingFormLabel" htmlFor="txtText" id="lUsername">
              E-mail address
            </label>{" "}
            <svg className="icomoon icomoon-user">
              <use xlinkHref="#icomoon-user" xmlnsXlink="http://www.w3.org/1999/xlink"></use>
            </svg>

            <div id="iUsername">
              <input className="form-control" id="txtText" maxLength={500} name="txtText" type="text" placeholder="E-mail address" />
            </div>
          </div>

          <div className="form-group">
            <label className="EditingFormLabel" htmlFor="txtPassword" id="lPassword">
              Password
            </label>{" "}
            <svg className="icomoon icomoon-lock">
              <use xlinkHref="#icomoon-lock" xmlnsXlink="http://www.w3.org/1999/xlink"></use>
            </svg>

            <div id="iPassword">
              <div>
                <input
                  className="form-control"
                  id="txtPassword"
                  maxLength={100}
                  name="txtPassword"
                  type="password"
                  placeholder="Password"
                />
              </div>
            </div>
            {showErrorMessage && (
            <span className="error" id="spanLoginSnipperError">
              E-mail address &amp; Password Combo is invalid
            </span>)}
          </div>

          <div className="form-group">
            <div className="forgot-password">
              <a className="more" href=" https://my.raps.org/account/login.aspx?forgot=yes&amp;RedirectUrl=https%3a%2f%2fraps.org&amp;reload=timezone">
                forgot your password?
              </a>
            </div>

            <div style={{ clear: "both" }}>&nbsp;</div>

            <hr />
          </div>

          <div className="hide">
            <table className="EditingFormTable">
              <tbody>
                <tr>
                  <td className="FieldLabel">&nbsp;</td>
                  <td className="EditingFormValueCell">
                    <input
                      className="FormButton hide btn btn-primary"
                      id="btnOK"
                      name="btnOK"
                      type="submit"
                      value="Login"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <input type="submit" value="Login" className="btn btn-default btn-responsive" id="btnSnippetLogin" />
          </form>
        </div>
        {showModal && <LoginHelpModal setShowModal={handleModalEvent} />}
      </div>
      <script>
      </script>
    </>
  );
};

export default ErrorLoginComponent;
